/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/immutability */
import { faClose, faSave, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickerValue } from "@mui/x-date-pickers/internals";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "../../../components/FormProvider";
import { Child, Family, ServicePeriod } from "../../../models";

interface EditServicePeriodFormProps {
  mode: "create" | "edit";
  families: Family[];
  children: Child[];
  servicePeriod: ServicePeriod | null;
  defaultFamilyId?: number;
  resetFormRef: React.RefObject<(() => void) | null>;
  dataGetterRef: React.RefObject<(() => ServicePeriod | null) | null>;
}

function dayjsToCentralDateTimeString(date: Dayjs | null, endOfDay: boolean): string {
  if (!date || !dayjs(date).isValid()) return "";
  const time = endOfDay ? "23:59:59" : "00:00:00";
  const formatted = dayjs(date).format(`YYYY-MM-DD`);
  const result = dayjs(`${formatted}T${time}`);
  return result.isValid() ? result.format("YYYY-MM-DDTHH:mm:ss") : "";
}

function startOfWeekMonday(value: Date): Date {
  const dayOfWeek = value.getDay();
  const daysSinceMonday = (dayOfWeek - 1 + 7) % 7;
  const date = new Date(value);
  date.setDate(date.getDate() - daysSinceMonday);
  return date;
}

function countBusinessWeeksEnrolled(child: any, periodStart: Date, periodEnd: Date): number {
  const enrollmentStart = child.enrollmentStartCentralDateTime ? new Date(child.enrollmentStartCentralDateTime) : new Date(-8640000000000000);
  const enrollmentEnd = child.enrollmentEndCentralDateTime ? new Date(child.enrollmentEndCentralDateTime) : new Date(8640000000000000);

  const effectiveStart = periodStart > enrollmentStart ? periodStart : enrollmentStart;
  const effectiveEnd = periodEnd < enrollmentEnd ? periodEnd : enrollmentEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  let count = 0;
  let weekStart = startOfWeekMonday(effectiveStart);

  while (weekStart <= effectiveEnd) {
    const businessStart = weekStart;
    const businessEnd = new Date(weekStart);
    businessEnd.setDate(businessEnd.getDate() + 4);

    const overlapStart = effectiveStart > businessStart ? effectiveStart : businessStart;
    const overlapEnd = effectiveEnd < businessEnd ? effectiveEnd : businessEnd;

    if (overlapStart <= overlapEnd) {
      count++;
    }

    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return count;
}

function calculateEstimatedTotalForChild(child: any, periodStart: Date, periodEnd: Date): number {
  const weekCount = countBusinessWeeksEnrolled(child, periodStart, periodEnd);
  return (child.weeklyRate ?? 0) * weekCount;
}

function getNextMonday(today: Date): Date {
  const date = new Date(today);
  const dayOfWeek = date.getDay();
  const daysUntilMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
  date.setDate(date.getDate() + daysUntilMonday);
  return date;
}

function getFridayWeeksOut(startDate: Date, weeksOut: number): Date {
  const date = new Date(startDate);
  date.setDate(date.getDate() + (weeksOut * 7 - 3)); // Add weeks, then move to Friday (3 days after Monday)
  return date;
}

function getFrequencyWeekCount(paymentFrequency: string | undefined): number {
  if (!paymentFrequency) return 1;
  const normalized = paymentFrequency.toLowerCase().replace(/[-\s]/g, "");
  if (normalized === "biweekly") return 2;
  if (normalized === "monthly") return 4;
  return 1; // default to weekly
}

export function EditServicePeriodForm(props: EditServicePeriodFormProps) {
  const { mode, families, children, servicePeriod, defaultFamilyId } = props;
  const form = useForm();

  const [familyId, setFamilyId] = useState<number>(servicePeriod?.familyId ?? defaultFamilyId ?? 0);
  const [startDate, setStartDate] = useState<Dayjs | null>(mode === "create" ? null : dayjs(servicePeriod?.startCentralDateTime));
  const [endDate, setEndDate] = useState<Dayjs | null>(mode === "create" ? null : dayjs(servicePeriod?.endCentralDateTime));

  useEffect(() => {
    if (mode === "edit") {
      setFamilyId(servicePeriod?.familyId ?? defaultFamilyId ?? 0);
      setStartDate(dayjs(servicePeriod?.startCentralDateTime));
      setEndDate(dayjs(servicePeriod?.endCentralDateTime));
    }
  }, [mode, servicePeriod, defaultFamilyId]);

  // Set default dates for create mode
  useEffect(() => {
    if (mode === "create" && familyId) {
      const selectedFamily = families.find((f) => f.id === familyId);
      if (selectedFamily) {
        const today = new Date();
        const nextMonday = getNextMonday(today);
        const weeksOut = getFrequencyWeekCount(selectedFamily.paymentFrequency);
        const friday = getFridayWeeksOut(nextMonday, weeksOut);

        setStartDate(dayjs(nextMonday));
        setEndDate(dayjs(friday));
      }
    }
  }, [mode, familyId, families]);

  const resetForm = (): void => {
    if (mode === "edit") {
      setFamilyId(servicePeriod?.familyId ?? defaultFamilyId ?? 0);
      setStartDate(dayjs(servicePeriod?.startCentralDateTime));
      setEndDate(dayjs(servicePeriod?.endCentralDateTime));
    } else {
      setFamilyId(defaultFamilyId ?? 0);
      setStartDate(null);
      setEndDate(null);
    }
  };

  const getUpdatedRecord = (): ServicePeriod | null => {
    if (!familyId) {
      return null;
    }

    const startCentralDateTime = dayjsToCentralDateTimeString(startDate, false);
    const endCentralDateTime = dayjsToCentralDateTimeString(endDate, true);

    if (!startCentralDateTime || !endCentralDateTime) {
      return null;
    }

    return {
      id: servicePeriod?.id ?? 0,
      familyId,
      startCentralDateTime,
      endCentralDateTime,
      receiptId: servicePeriod?.receiptId,
      createdCentralDateTime: servicePeriod?.createdCentralDateTime ?? "2000-01-01",
      modifiedCentralDateTime: servicePeriod?.modifiedCentralDateTime ?? "2000-01-01",
      deleted: false,
      family: undefined,
      lines: [],
    };
  };

  useEffect(() => {
    props.dataGetterRef.current = getUpdatedRecord;
    props.resetFormRef.current = resetForm;
  });

  const handleFamilyChange = (event: SelectChangeEvent<number>) => {
    setFamilyId(Number(event.target.value));
  };

  const handleStartDateChange = (value: PickerValue) => {
    const newStartDate = dayjs(value);
    setStartDate(newStartDate);
    // If start date is after end date, update end date to match start date
    if (endDate && newStartDate.isValid() && endDate.isValid() && newStartDate.isAfter(endDate)) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (value: PickerValue) => {
    const newEndDate = dayjs(value);
    setEndDate(newEndDate);
    // If end date is before start date, update start date to match end date
    if (startDate && newEndDate.isValid() && startDate.isValid() && newEndDate.isBefore(startDate)) {
      setStartDate(newEndDate);
    }
  };

  const estimatedTotal = useMemo(() => {
    // Check if dates are valid Dayjs objects and have actual values
    if (!startDate || !endDate || !startDate.isValid?.() || !endDate.isValid?.()) {
      return "$0.00";
    }

    const periodStart = startDate.toDate();
    const periodEnd = endDate.toDate();

    if (mode === "edit") {
      // For edit mode, calculate based on existing lines
      if (!servicePeriod?.lines || servicePeriod.lines.length === 0) {
        return "$0.00";
      }

      const total = servicePeriod.lines
        .filter((line) => !line.deleted && line.child)
        .reduce((sum, line) => {
          const lineTotal = calculateEstimatedTotalForChild(line.child, periodStart, periodEnd);
          return sum + lineTotal;
        }, 0);

      return total.toLocaleString("en-US", { style: "currency", currency: "USD" });
    } else {
      // For create mode, calculate based on active children in the selected family
      if (!familyId || !children) {
        return "$0.00";
      }

      const familyChildren = children.filter((child) => child.familyId === familyId && child.isActive && !child.deleted);
      if (familyChildren.length === 0) {
        return "$0.00";
      }

      const total = familyChildren.reduce((sum, child) => {
        const lineTotal = calculateEstimatedTotalForChild(child, periodStart, periodEnd);
        return sum + lineTotal;
      }, 0);

      return total.toLocaleString("en-US", { style: "currency", currency: "USD" });
    }
  }, [mode, servicePeriod, startDate, endDate, familyId, children]);

  const estimatedLineBreakdown = useMemo(() => {
    // Check if dates are valid Dayjs objects and have actual values
    if (!startDate || !endDate || !startDate.isValid?.() || !endDate.isValid?.()) {
      return [];
    }

    const periodStart = startDate.toDate();
    const periodEnd = endDate.toDate();

    if (mode === "edit") {
      // For edit mode, calculate based on existing lines
      if (!servicePeriod?.lines || servicePeriod.lines.length === 0) {
        return [];
      }

      return servicePeriod.lines
        .filter((line) => !line.deleted && line.child)
        .map((line) => {
          const child = line.child!;
          const weeks = countBusinessWeeksEnrolled(child, periodStart, periodEnd);
          const amount = calculateEstimatedTotalForChild(child, periodStart, periodEnd);
          return {
            childName: child.firstName,
            weeks,
            amount,
          };
        });
    } else {
      // For create mode, calculate based on active children in the selected family
      if (!familyId || !children) {
        return [];
      }

      const familyChildren = children.filter((child) => child.familyId === familyId && child.isActive && !child.deleted);
      if (familyChildren.length === 0) {
        return [];
      }

      return familyChildren.map((child) => {
        const weeks = countBusinessWeeksEnrolled(child, periodStart, periodEnd);
        const amount = calculateEstimatedTotalForChild(child, periodStart, periodEnd);
        return {
          childName: child.firstName,
          weeks,
          amount,
        };
      });
    }
  }, [mode, servicePeriod, startDate, endDate, familyId, children]);

  return (
    <Stack gap={2} sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {mode === "create" ? "Add Service Period" : "Edit Service Period"}
        </Typography>
        <IconButton sx={{ width: 44, height: 44 }} onClick={form.cancel}>
          <FontAwesomeIcon icon={faClose} />
        </IconButton>
      </Stack>

      <Stack gap={1} sx={{ flexGrow: 1, overflow: "auto", minHeight: 0 }}>
        <FormControl fullWidth variant="filled" disabled={form.isDisabled || mode === "edit"}>
          <InputLabel id="service-period-family-label">Family</InputLabel>
          <Select
            labelId="service-period-family-label"
            label="Family"
            value={familyId || ""}
            onChange={handleFamilyChange}
            MenuProps={{
              slotProps: {
                root: { sx: { zIndex: 1600 } },
                paper: { sx: { zIndex: 1600 } },
              },
            }}
          >
            {families.map((family) => (
              <MenuItem key={family.id} value={family.id}>
                {family.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" gap={1} sx={{ width: "100%" }}>
          <DatePicker
            onChange={handleStartDateChange}
            value={startDate}
            label="Service Start"
            sx={{ flex: 1 }}
            slotProps={{
              textField: {
                variant: "filled",
              },
            }}
            disabled={form.isDisabled}
          />
          <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>&mdash;</Box>
          <DatePicker
            onChange={handleEndDateChange}
            value={endDate}
            label="Service End"
            sx={{ flex: 1 }}
            slotProps={{
              textField: {
                variant: "filled",
              },
            }}
            disabled={form.isDisabled}
          />
        </Stack>

        <Stack gap={0.5} sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, mb: 1 }}>
            Estimated Total
          </Typography>
          {estimatedLineBreakdown.length > 0 ? (
            <>
              {estimatedLineBreakdown.map((line, index) => (
                <Stack direction="row" justifyContent="space-between" key={index} gap={2}>
                  <Typography variant="body2">
                    {line.childName} × {line.weeks} week{line.weeks !== 1 ? "s" : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {line.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                  </Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between" gap={2} sx={{ pt: 1, borderTop: 1, borderTopColor: "divider", mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Total
                </Typography>
                <Typography variant="subtitle2" sx={{ textAlign: "right", fontWeight: 600 }}>
                  {estimatedTotal}
                </Typography>
              </Stack>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No service period details
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack gap={2} sx={{ flexShrink: 0 }}>
        <Button variant="contained" onClick={form.save} sx={{ height: 50, width: "100%" }} startIcon={<FontAwesomeIcon icon={faSave} />}>
          Save
        </Button>

        <Button variant="outlined" onClick={form.cancel} sx={{ height: 50, width: "100%" }} startIcon={<FontAwesomeIcon icon={faTimes} />}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}
